<template>
  <div
    :style="{
      background: cssBackground,
      color: micrositeDetails.textColor,
      fontFamily: `${micrositeDetails.fontFamily}, ui-sans-serif, system-ui, sans-serif`,
      '--microsite-header-border-radius': buttonBorderRadius,
      '--microsite-header-background-color':
        micrositeDetails.headerBackgroundColor,
      '--microsite-header-box-shadow': `0px 0px 15px 2px rgba(${hexToRgb(props.micrositeDetails.headerBackgroundColor).join(', ')}, 0.3)`,
      '--microsite-button-border-radius': buttonBorderRadius,
      '--microsite-button-box-shadow': buttonBoxShadow,
      '--microsite-button-background-color':
        micrositeDetails.buttonBackgroundColor,
      '--microsite-button-text-color': micrositeDetails.buttonTextColor,
    }"
  >
    <!--Safe area padding for devices showing content outside safe area.-->
    <div class="pb-[env(safe-area-inset-top)]" />
    <div class="mx-auto flex max-w-lg flex-col px-4 pb-12 pt-4">
      <div
        :class="{
          'rounded-[var(--microsite-header-border-radius)] bg-[var(--microsite-header-background-color)] p-4':
            micrositeDetails.headerCustomColorsEnabled,
          '[box-shadow:var(--microsite-button-box-shadow)]':
            micrositeDetails.buttonShadow !== MicrositeButtonShadow.NONE &&
            micrositeDetails.headerCustomColorsEnabled,
          '[box-shadow:var(--microsite-header-box-shadow)]':
            micrositeDetails.buttonShadow === MicrositeButtonShadow.NONE &&
            micrositeDetails.headerCustomColorsEnabled,
          'pt-4': !micrositeDetails.headerCustomColorsEnabled,
        }"
      >
        <div
          v-if="micrositeDetails.logoUrl"
          class="mb-5 flex h-20 items-center justify-center"
        >
          <img
            :key="micrositeDetails.logoUrl"
            :src="micrositeDetails.logoUrl"
            class="max-h-full w-auto"
            alt=""
          />
        </div>

        <h1
          v-if="micrositeDetails.title"
          class="text-center text-xl font-semibold"
        >
          {{ micrositeDetails.title }}
        </h1>

        <div
          v-if="micrositeDetails.bio"
          class="mt-2 whitespace-pre-line break-words text-center"
          v-text="micrositeDetails.bio"
        />

        <MicrositeRendererSocialLinks
          v-if="
            micrositeDetails.socialLinksPosition ===
            MicrositeSocialLinksPosition.TOP
          "
          :microsite-details="micrositeDetails"
          class="mt-4"
        />

        <div
          v-if="micrositeDetails.textingCtaEnabled"
          class="mt-4 flex flex-col space-y-2 empty:hidden"
        >
          <MicrositeRendererButtonLink
            :href="
              micrositeDetails.textingCtaNumber
                ? generateSmsLink(micrositeDetails.textingCtaNumber, '')
                : '#'
            "
            :icon="ChatCircleTextRegular"
            :small="isTextingCtaSmall"
            :class="{
              'mx-auto w-max max-w-full': isTextingCtaSmall,
            }"
          >
            {{ micrositeDetails.textingCtaButtonText }}
          </MicrositeRendererButtonLink>

          <div
            v-if="micrositeDetails.textingCtaHelpText"
            class="text-center"
            :class="isTextingCtaSmall ? 'text-xs' : 'text-sm'"
          >
            {{ micrositeDetails.textingCtaHelpText }}
          </div>
        </div>
      </div>

      <div class="mt-6 flex flex-col space-y-3">
        <MicrositeRendererButtonLink
          v-for="(
            micrositeDetailsItem, micrositeDetailsItemIndex
          ) in micrositeDetails.items"
          :key="micrositeDetailsItemIndex"
          :href="micrositeDetailsItem.url ?? '#'"
          :target="getTargetForMicrositeDetailsItem(micrositeDetailsItem)"
          :icon="getIconForMicrositeDetailsItem(micrositeDetailsItem)"
          @click="emit('micrositeDetailsItemClicked', micrositeDetailsItem)"
        >
          {{ micrositeDetailsItem.title }}
        </MicrositeRendererButtonLink>
      </div>

      <MicrositeRendererSocialLinks
        v-if="
          micrositeDetails.socialLinksPosition ===
          MicrositeSocialLinksPosition.BOTTOM
        "
        :microsite-details="micrositeDetails"
        class="mt-4"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import {
  MicrositeDetails,
  MicrositeDetailsItem,
} from '@web/types/objects/MicrositeDetails';
import ChatCircleTextRegular from '@web/components/utilities/svg/phosphorIcons/ChatCircleTextRegular.vue';
import generateSmsLink from '@web/utils/generateSmsLink';
import { MicrositeItemActionType } from '@web/enums/MicrositeItemActionType';
import PlayCircleRegular from '@web/components/utilities/svg/phosphorIcons/PlayCircleRegular.vue';
import MicrositeRendererButtonLink from '@web/components/microsite/MicrositeRendererButtonLink.vue';
import getButtonBorderRadiusByButtonShape from '@web/components/microsite/getButtonBorderRadiusByButtonShape';
import getButtonBoxShadowByButtonShadow from '@web/components/microsite/getButtonBoxShadowByButtonShadow';
import { computed } from 'vue';
import hexToRgb from '@web/utils/hexToRgb';
import generateMicrositeCssBackground from '@web/components/microsite/generateMicrositeCssBackground';
import MicrositeRendererSocialLinks from '@web/components/microsite/MicrositeRendererSocialLinks.vue';
import { MicrositeSocialLinksPosition } from '@web/enums/MicrositeSocialLinksPosition';
import { MicrositeButtonShadow } from '@web/enums/MicrositeButtonShadow';
import generateMicrositeBackgroundColorsFromBase from '@web/components/microsite/generateMicrositeBackgroundColorsFromBase';

const props = defineProps<{
  micrositeDetails: MicrositeDetails;
  withoutBackground?: boolean;
}>();

const emit = defineEmits<{
  micrositeDetailsItemClicked: [micrositeDetailsItem: MicrositeDetailsItem];
}>();

const isTextingCtaSmall = computed(() => {
  return props.micrositeDetails.headerCustomColorsEnabled;
});

const buttonBorderRadius = computed(() => {
  return getButtonBorderRadiusByButtonShape(props.micrositeDetails.buttonShape);
});

const buttonBoxShadow = computed(() => {
  return getButtonBoxShadowByButtonShadow(props.micrositeDetails.buttonShadow);
});

function getTargetForMicrositeDetailsItem(
  micrositeDetailsItem: MicrositeDetailsItem,
): string {
  if (micrositeDetailsItem.url === null) {
    return '_self';
  }

  try {
    const urlObj = new URL(micrositeDetailsItem.url);

    // Clearstream hosts a helper endpoint (`sms.clearstream.io`) that converts
    // HTTPS links such as `https://sms.clearstream.io/97000/join` into `sms:`
    // protocol links like `sms:97000?&body=join`. When opened in a new tab,
    // some browsers display the "Open this page in 'Messages'?" prompt only
    // after the tab is manually activated. To prevent this, we open it in the
    // same tab for a smoother user experience.
    if (urlObj.hostname === 'sms.clearstream.io') {
      return '_self';
    }
  } catch {
    //
  }

  return '_blank';
}

function getIconForMicrositeDetailsItem(
  micrositeDetailsItem: MicrositeDetailsItem,
) {
  return {
    [MicrositeItemActionType.VIDEO]: PlayCircleRegular,
  }[micrositeDetailsItem.type];
}

const backgroundColors = computed(() =>
  generateMicrositeBackgroundColorsFromBase(
    props.micrositeDetails.backgroundColor,
    props.micrositeDetails.backgroundStyle,
  ),
);

const cssBackground = computed(() => {
  if (props.withoutBackground) {
    return;
  }

  return generateMicrositeCssBackground(backgroundColors.value);
});
</script>
